/**
 * NBT cart giveaway entries presentation.
 *
 * Scott Tompkins (SJT)
 *
 * Integration boundary:
 * - Read-only. Reads Platter's existing Alpine cart store; never mutates cart
 *   state, discounts, bundles, gifts, upsells or checkout.
 * - Does not touch the savings layer (window.djGiardcoCartSavings, Don Price /
 *   dj-giardco). Giveaway entries are a separate capability by design.
 * - Viralsweep records official entries. This is display only.
 *
 * Where the number comes from
 * ------------------------------------------------------------------
 * Giveaway entries live on the product metafield smart.giveaway_entry, and
 * metafields are readable ONLY in Liquid — Shopify's cart AJAX object does not
 * include them. So the total is computed server-side in the block's Liquid and
 * printed into a JSON payload. This file just reads that payload. It performs
 * NO entry arithmetic, which also means the count cannot be tampered with
 * client-side.
 *
 * Why there is no fetching here
 * ------------------------------------------------------------------
 * The theme already re-renders the cart drawer server-side after a cart change,
 * so a fresh Liquid-computed payload arrives on its own. An earlier version of
 * this file tried to re-fetch the section itself via the Section Rendering API.
 * That was wrong twice over: the drawer lives in a section group, so
 * ?section_id= returns 404 and ?sections=main_cart renders an instance with no
 * blocks; and the failing request made the block hide itself for a moment
 * before the theme's own re-render brought it back. Removing the fetch removes
 * the flash and the failure mode together.
 *
 * Extending this for other campaign types (spend rewards, multipliers,
 * milestones): add the field to the block's Liquid payload and expose it as a
 * getter below. Keep the arithmetic in Liquid.
 */
(() => {
  "use strict";

  // SJT: Avoid replacing an existing helper if Theme Editor hot reload
  // evaluates this asset more than once. Same guard the savings layer uses.
  if (window.sjtNbtCartGiveaway) return;

  const toNumber = (value, fallback = 0) => {
    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) ? parsedValue : fallback;
  };

  const cleanText = (value, fallback = "") => {
    const text = String(value ?? "").trim();
    return text || fallback;
  };

  const getCartState = (cartStore) => cartStore?.state ?? {};

  /**
   * SJT: Mirrors the cart_signature format the block's Liquid prints, so we can
   * tell whether the payload we are showing describes the cart as it is now.
   */
  const buildCartSignature = (cartState) =>
    (cartState?.items ?? []).map((item) => `${item?.key}:${toNumber(item?.quantity)}`).join(";");

  const readConfiguration = (element) => {
    const dataset = element?.dataset ?? {};

    return {
      headingSingular: cleanText(dataset.headingSingular, "entry"),
      headingPlural: cleanText(dataset.headingPlural, "entries"),
      minimumToShow: Math.max(0, toNumber(dataset.minimumToShow, 1)),
      presentation: cleanText(dataset.presentation, "page"),
    };
  };

  window.sjtNbtCartGiveaway = {
    /**
     * SJT: Alpine-friendly state. Each server render of the drawer mounts a
     * fresh component whose init() reads that render's payload, so the number
     * tracks the cart without this file doing any work.
     */
    create(element, cartStore) {
      const configuration = readConfiguration(element);

      return {
        rootElement: element,
        cartStore,
        configuration,

        // SJT: null means "not known yet" — deliberately distinct from 0, so
        // the block stays silent rather than claiming zero entries.
        serverTotalEntries: null,

        // SJT: entries one unit of each cart line is worth, from Liquid.
        entriesByLine: null,

        // SJT: last number we were able to stand behind. Held so a cart line we
        // have no value for (a product added while the drawer is open) shows the
        // previous count rather than blinking out; it corrects on next render.
        lastKnownTotal: null,

        // SJT: the cart the current number describes, so we can tell whether it
        // has since gone stale.
        payloadSignature: null,

        get cartState() {
          return getCartState(this.cartStore);
        },

        get cartSignature() {
          return buildCartSignature(this.cartState);
        },

        get isEmptyCart() {
          return toNumber(this.cartState?.item_count) <= 0;
        },

        /**
         * SJT: The live total. Each line's per-unit value came from Liquid
         * reading the metafield; all this does is multiply by the quantity
         * Alpine already holds and add them up. That is what makes the count
         * follow quantity changes while the drawer stays open.
         *
         * Returns null if any cart line has no known per-unit value — a product
         * added while the drawer is open. Guessing there would be worse than
         * briefly showing the previous number.
         */
        get liveTotalEntries() {
          const map = this.entriesByLine;
          if (!map) return null;

          const items = this.cartState?.items ?? [];
          let total = 0;

          for (const item of items) {
            const each = map[item?.key];
            if (each === undefined) return null;
            total += toNumber(each) * toNumber(item?.quantity);
          }

          return total;
        },

        get totalEntries() {
          const live = this.liveTotalEntries;
          if (live !== null) return live;
          return this.lastKnownTotal ?? this.serverTotalEntries;
        },

        get isKnown() {
          return this.totalEntries !== null;
        },

        get entriesLabel() {
          return this.totalEntries === 1
            ? this.configuration.headingSingular
            : this.configuration.headingPlural;
        },

        /**
         * SJT: Visibility. Deliberately does NOT hide while the cart is mid-
         * update. Between a quantity change and the theme's re-render the
         * number can be a second out of date, and briefly showing the previous
         * count reads far better than the block blinking out and back. This is
         * a marketing display, not the system of record — Viralsweep is — so a
         * moment of lag is the right trade. An empty cart still hides
         * immediately, because zero entries is a real answer.
         */
        get shouldShow() {
          if (this.isEmptyCart) return false;
          if (!this.isKnown) return false;
          return this.totalEntries >= this.configuration.minimumToShow;
        },

        /**
         * SJT: True when the cart has moved on from what this number describes.
         * Not used to hide the block (see shouldShow) — exposed so the markup
         * can dim it slightly, and so future campaign types can decide for
         * themselves how much staleness they tolerate.
         */
        get isStale() {
          if (!this.payloadSignature) return false;
          return this.payloadSignature !== this.cartSignature;
        },

        /**
         * SJT: The block is server-rendered with the correct total already, so
         * read it inline. No request, no flash, no failure mode.
         */
        readInlinePayload() {
          const payloadNode = this.rootElement?.querySelector(
            "[data-sjt-giveaway-entries-payload]",
          );

          if (!payloadNode) return false;

          try {
            const payload = JSON.parse(payloadNode.textContent);
            this.serverTotalEntries = Math.max(0, toNumber(payload.total_entries));
            this.entriesByLine =
              payload.entries_by_line && typeof payload.entries_by_line === "object"
                ? payload.entries_by_line
                : null;
            this.payloadSignature = cleanText(payload.cart_signature) || null;
            this.lastKnownTotal = this.serverTotalEntries;
            return true;
          } catch (error) {
            return false;
          }
        },

        init() {
          this.readInlinePayload();

          // SJT: Remember every total we can stand behind, so an unknown line
          // falls back to the last good number instead of nothing.
          this.$watch("liveTotalEntries", (value) => {
            if (value !== null) this.lastKnownTotal = value;
          });
        },
      };
    },
  };
})();
